import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next/typescript/t";

import { useLoginMutation } from "@/redux/service";
import { setAuthenticationState } from "@/redux/authentication/reducer";
import { LocalStorageName } from "@/redux/api";
import { LoginRequest } from "@/redux/authentication/types";
import { handleErrors } from "@/shared/utils/forms";
import { HookFormInput } from "@/components_v2/FormFields/ReactHookForm/fields";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import BaseButton from "@/components_v2/Button/BaseButton";

import "./styles.css";

const loginSchema = (t: TFunction): yup.ObjectSchema<LoginRequest> =>
  yup.object().shape({
    email: yup
      .string()
      .typeError(t("auth.validation.emailNotValid"))
      .email(t("auth.validation.enterValidEmail"))
      .required(t("auth.validation.enterValidEmail")),
    password: yup
      .string()
      .typeError(t("auth.validation.passwordNotValid"))
      .required(t("auth.validation.passwordRequired")),
  });

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notification } = App.useApp();
  const [login, { isLoading }] = useLoginMutation();

  const { t } = useTranslation();

  const form = useForm<LoginRequest>({
    resolver: yupResolver(loginSchema(t)),
    mode: "onChange",
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = (values: LoginRequest) => {
    login(values)
      .unwrap()
      .then((data) => {
        dispatch(
          setAuthenticationState({
            isAuthenticated: true,
            user: data.user,
            isFirstLogin: data.isFirstLogin,
          }),
        );
        window.localStorage.setItem(
          LocalStorageName,
          JSON.stringify({
            accessExpiration: data.accessExpiration,
            refreshExpiration: data.refreshExpiration,
            user: data.user,
          }),
        );
        navigate("/");
      })
      .catch((errors) => {
        handleErrors(errors, form.setError, notification);
        if (errors?.data?.errors?.[0]?.code === "email_verification") {
          navigate("/email-verification");
        }
      });
  };

  return (
    <div className="flex flex-col gap-400">
      <h2 className="header-l-medium text-center">
        {t("auth.labels.signInTitle")}
      </h2>

      <div className="form-input-container">
        <FormFieldWrapper label={t("auth.labels.email")} required>
          <HookFormInput name="email" control={form.control} />
        </FormFieldWrapper>

        <div className="flex flex-col gap-200">
          <FormFieldWrapper label={t("auth.labels.password")} required>
            <HookFormInput
              type="password"
              name="password"
              control={form.control}
            />
          </FormFieldWrapper>

          <div className="text-end">
            <Link to="/reset-password" className="link-text">
              {t("auth.labels.forgotPassword")}
            </Link>
          </div>
        </div>

        <div className="flex flex-col items-center">
          <BaseButton
            disabled={isLoading}
            isFullWidth
            onClick={form.handleSubmit(onSubmit)}
          >
            {t("auth.labels.signIn")}
          </BaseButton>
        </div>
      </div>
    </div>
  );
};

export default Login;
